import { useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, MinusCircle, Loader2, Download, RefreshCw, Trash2, FileText, UserCheck, Upload } from 'lucide-react';
import Button from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import type { CheckStatus, Doc, Slot, SlotStatus } from '@/lib/documents';

const fmt = (d?: string | null) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const SLOT_TONE: Record<SlotStatus, string> = {
  missing: 'border-slate-700 text-muted',
  checking: 'border-sky-700/60 text-sky-300',
  checked: 'border-emerald-700/60 text-emerald-300',
  warnings: 'border-amber-700/60 text-amber-300',
  mismatch: 'border-red-700/60 text-red-300',
  expiring: 'border-amber-700/60 text-amber-300',
  expired: 'border-red-700/60 text-red-300',
};

export function slotText(s: { status: SlotStatus; days_left?: number | null }): string {
  switch (s.status) {
    case 'missing': return 'Missing';
    case 'checking': return 'Received, checking';
    case 'checked': return 'Received and checked';
    case 'warnings': return 'Received with warnings';
    case 'mismatch': return 'Received, does not match';
    case 'expiring': return s.days_left === 0 ? 'Expires today' : `Expires in ${s.days_left} days`;
    case 'expired': return 'Expired';
  }
}
const SLOT_AR: Record<SlotStatus, string> = {
  missing: 'غير مستلم', checking: 'قيد التحقق', checked: 'مستلم ومتحقق منه', warnings: 'مستلم مع ملاحظات', mismatch: 'مستلم، غير مطابق',
  expiring: 'ينتهي قريباً', expired: 'منتهي',
};

/** "To start with this client": every required document and where it stands. */
export function Checklist({ slots, onUpload, canUpload, title = 'To start with this client', titleAr = 'للبدء مع هذا العميل' }: {
  slots: Slot[]; onUpload?: (s: Slot) => void; canUpload: boolean; title?: string; titleAr?: string;
}) {
  const done = slots.filter((s) => s.status === 'checked' || s.status === 'warnings' || s.status === 'mismatch' || s.status === 'checking' || s.status === 'expiring').length;
  return (
    <div data-testid="doc-checklist">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <p className="text-xs text-muted" dir="rtl">{titleAr}</p>
        </div>
        <span className="text-sm text-muted">{done} of {slots.length} received</span>
      </div>
      <ul className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2">
        {slots.map((s) => (
          <li key={s.key} className={cn('rounded-lg border bg-bg/50 p-3 flex flex-col gap-1', SLOT_TONE[s.status])} data-testid={`slot-${s.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm text-muted-strong">{s.label_en}</div>
                <div className="text-xs text-muted" dir="rtl">{s.label_ar}</div>
              </div>
              <StatusIcon status={s.status} />
            </div>
            <div className="flex items-center justify-between gap-2 mt-1">
              <span className="text-xs">{slotText(s)} <span className="text-muted">| {SLOT_AR[s.status]}</span></span>
              {canUpload && onUpload && (s.status === 'missing' || s.status === 'expired' || s.status === 'expiring' || s.status === 'mismatch') && (
                <Button size="sm" variant="secondary" icon={<Upload size={12} />} onClick={() => onUpload(s)} data-testid={`slot-upload-${s.key}`}>Upload</Button>
              )}
            </div>
            {s.suggest_name_en && s.status === 'missing' && <div className="text-[11px] text-muted">Usually: {s.suggest_name_en}</div>}
            {!s.verified && <div className="text-[11px] text-amber-300">Unverified requirement: {s.note_en}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusIcon({ status }: { status: SlotStatus }) {
  if (status === 'checked') return <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />;
  if (status === 'checking') return <Loader2 size={16} className="text-sky-400 animate-spin shrink-0" />;
  if (status === 'warnings' || status === 'expiring') return <AlertTriangle size={16} className="text-amber-400 shrink-0" />;
  if (status === 'expired' || status === 'mismatch') return <XCircle size={16} className="text-red-400 shrink-0" />;
  return <MinusCircle size={16} className="text-slate-500 shrink-0" />;
}

const CHECK_TONE: Record<CheckStatus, string> = {
  pass: 'text-emerald-300', warn: 'text-amber-300', fail: 'text-red-300', skip: 'text-muted',
};
const CHECK_ICON: Record<CheckStatus, JSX.Element> = {
  pass: <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-0.5" />,
  warn: <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />,
  fail: <XCircle size={14} className="text-red-400 shrink-0 mt-0.5" />,
  skip: <MinusCircle size={14} className="text-slate-500 shrink-0 mt-0.5" />,
};
const OVERALL: Record<string, string> = {
  pass: 'Every check passed', warn: 'Check the warnings', fail: 'Something does not match',
};

/** The automatic check of one document: each check green, amber or red. */
export function ResultCard({ doc, onText, onChecked, onRecheck }: {
  doc: Doc; onText?: () => Promise<string>; onChecked?: () => void; onRecheck?: () => void;
}) {
  const [text, setText] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  if (doc.ocr_status === 'hidden') return null;
  if (doc.ocr_status === 'pending' || doc.ocr_status === 'running') {
    return (
      <div className="rounded-lg border border-sky-800/60 bg-sky-950/20 p-3 text-sm text-sky-300 flex items-center gap-2" data-testid="doc-checking">
        <Loader2 size={14} className="animate-spin" /> Checking the document... <span className="text-xs text-muted" dir="rtl">جارٍ التحقق من المستند</span>
      </div>
    );
  }
  const v = doc.validation || {};
  const overall = v.overall ?? 'warn';
  const ex = v.extracted ?? {};
  return (
    <div className={cn('rounded-lg border p-3 text-sm', overall === 'pass' ? 'border-emerald-800/60 bg-emerald-950/10'
      : overall === 'fail' ? 'border-red-800/60 bg-red-950/10' : 'border-amber-800/60 bg-amber-950/10')} data-testid="doc-result">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className={cn('font-medium', CHECK_TONE[overall as CheckStatus])}>{OVERALL[overall] ?? 'Checked'}</div>
        {doc.checked_by_name ? (
          <span className="text-xs text-emerald-300 inline-flex items-center gap-1"><UserCheck size={12} /> Checked by {doc.checked_by_name}{doc.checked_at ? `, ${fmt(doc.checked_at)}` : ''}</span>
        ) : onChecked && (
          <Button size="sm" variant="secondary" icon={<UserCheck size={12} />} onClick={onChecked} data-testid="doc-mark-checked">Checked by a person</Button>
        )}
      </div>
      {doc.check_note && <div className="text-xs text-muted mt-1">{doc.check_note}</div>}
      <ul className="mt-2 grid gap-1">
        {(v.checks ?? []).map((c) => (
          <li key={c.key} className="flex gap-2"><span>{CHECK_ICON[c.status]}</span>
            <span><span className={cn('font-medium', CHECK_TONE[c.status])}>{c.label}:</span> <span className="text-muted-strong">{c.detail}</span></span></li>
        ))}
      </ul>
      {(ex.numbers?.length || ex.dates?.length) ? (
        <div className="mt-2 text-xs text-muted grid gap-0.5">
          {ex.numbers?.length ? <div>Numbers read: <span className="text-muted-strong">{ex.numbers.slice(0, 5).join(', ')}</span></div> : null}
          {ex.dates?.length ? <div>Dates read: <span className="text-muted-strong">{ex.dates.map((d) => fmt(d)).join(', ')}</span>
            {ex.issue && <> (issue {fmt(ex.issue)})</>}{ex.expiry && <> (expiry {fmt(ex.expiry)})</>}</div> : null}
        </div>
      ) : null}
      <div className="mt-2 flex gap-2 flex-wrap">
        {onText && (
          <Button size="sm" variant="ghost" icon={<FileText size={12} />} data-testid="doc-toggle-text"
            onClick={async () => { if (!open && text === null) setText(await onText()); setOpen(!open); }}>
            {open ? 'Hide extracted text' : 'View extracted text'}
          </Button>
        )}
        {onRecheck && <Button size="sm" variant="ghost" icon={<RefreshCw size={12} />} onClick={onRecheck}>Run the check again</Button>}
      </div>
      {open && text !== null && (
        <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-bg/80 border border-slate-800 p-2 text-xs text-muted-strong" dir="auto" data-testid="doc-text">{text || 'No text could be read.'}</pre>
      )}
    </div>
  );
}

const EXPIRY_TONE = { valid: 'text-emerald-300', expiring: 'text-amber-300', expired: 'text-red-300', no_expiry: 'text-muted' };
export function expiryText(d: Doc): string {
  if (d.expiry_status === 'no_expiry') return 'No expiry';
  if (d.expiry_status === 'expired') return `Expired ${fmt(d.expiry_date)}`;
  if (d.expiry_status === 'expiring') return d.days_left === 0 ? 'Expires today' : `Expires in ${d.days_left} days`;
  return `Valid until ${fmt(d.expiry_date)}`;
}

/** One current document: its details, its check, and what the person may do with it. */
export function DocCard({ doc, onOpen, onReplace, onDelete, result }: {
  doc: Doc; onOpen: (id: string) => void; onReplace?: () => void; onDelete?: () => void; result?: JSX.Element | null;
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-bg/40 p-3 sm:p-4 grid gap-3" data-testid={`doc-${doc.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-medium text-white break-words">{doc.title}</div>
          {doc.name_ar && <div className="text-xs text-muted" dir="rtl">{doc.name_ar}</div>}
          <div className="text-xs text-muted mt-0.5">{doc.authority_en ?? ''}{doc.from_client && <span className="ml-2 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-muted-strong">From the clinic</span>}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="secondary" icon={<Download size={12} />} onClick={() => onOpen(doc.id)} data-testid="doc-download">Download</Button>
          {onReplace && <Button size="sm" variant="secondary" icon={<RefreshCw size={12} />} onClick={onReplace} data-testid="doc-replace">Replace</Button>}
          {onDelete && <Button size="sm" variant="ghost" icon={<Trash2 size={12} />} onClick={onDelete} data-testid="doc-delete">Delete</Button>}
        </div>
      </div>
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs">
        <div><dt className="text-muted">Number</dt><dd className="text-muted-strong break-all">{doc.number || '-'}</dd></div>
        <div><dt className="text-muted">Issued</dt><dd className="text-muted-strong">{fmt(doc.issue_date) || '-'}</dd></div>
        <div><dt className="text-muted">Expiry</dt><dd className={EXPIRY_TONE[doc.expiry_status]}>{expiryText(doc)}</dd></div>
        <div><dt className="text-muted">Uploaded</dt><dd className="text-muted-strong">{doc.uploaded_by_name ?? 'Someone'}, {fmt(doc.uploaded_at)}</dd></div>
      </dl>
      {doc.notes && <p className="text-xs text-muted whitespace-pre-wrap">{doc.notes}</p>}
      {!doc.verified && doc.hint && <p className="text-xs text-amber-300">Unverified requirement: {doc.hint}</p>}
      {result}
      {doc.history.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-accent-400">Earlier versions ({doc.history.length})</summary>
          <ul className="mt-2 grid gap-1">
            {doc.history.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-2 border-t border-slate-800 pt-1">
                <span className="text-muted">{h.filename}, {h.uploaded_by_name ?? 'someone'}, {fmt(h.uploaded_at)}{h.expiry_date ? `, expiry ${fmt(h.expiry_date)}` : ''}</span>
                <button className="text-accent-400 hover:underline" onClick={() => onOpen(h.id)}>Download</button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
